import React, { useEffect } from 'react'
import DashboardBackground from '../../components/ReUsableComponents/DashboardBackground'
import SideBarV1 from '../NewDashboard/SideBarV1'
import HeaderV1 from '../NewDashboard/HeaderV1'
import { useGetPlanDetailsQuery, useLazyDownloadInvoiceQuery } from '../../redux/API/Payments/paymentsapi'
import FullMarksLoader from '../../components/ReUsableComponents/FullMarksLoader'
import { BiCheckCircle, BiDownload, BiHistory, BiX } from 'react-icons/bi'
import AuthButton from '../LogInPage/LoginButton'
import { setSelectedPlan } from '../../redux/Slices/pricingSlice'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { getItem, removeItem, setItem } from '../../utils/token'
import { RootState } from '../../redux/Store/store'
import { downloadBlobFile, handleTokenChanges } from '../../utils'
import { toast } from 'react-toastify'
import { decode } from "js-base64";

type Feature = {
    name: string;
    isOffered: boolean;
};
  
type PackageDetails = {
    validity: number;
    includedTestsCount: number;
    targetAudience: string;
    savings: string | null;
    features: Feature[];
    additionalPros: any | null;
};

export type Plan = {
    id: string;
    isActive: boolean;
    createdDate: string;
    updatedDate: string | null;
    planName: string;
    price: number;
    currency: string;
    currencySymbol: string;
    planLogo: string;
    planCategory: string;
    packageDetails: PackageDetails;
    additionalProps: any | null;
};

type ActivePlan = {
    planName: string;
    isActive: boolean;
    dateOfPurchase: string;
    testsRemaining: number;
    planEndDate: string;
    transactionId: string;
}

const Plans = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { token } = useParams();

    const {parentStudentId,startedTestId} = useSelector((state : RootState) => state.root.startFullTest);

    const { data: planData, isLoading} = useGetPlanDetailsQuery({userId:getItem("userRole") === "PARENT" ? parentStudentId : getItem("userId"),product:'SAT'},{refetchOnMountOrArgChange:true});
    const [downloadInvoice,{isLoading:invoiceLoading}]  = useLazyDownloadInvoiceQuery();

    const activePlanData = planData?.data?.activePlan && Object.keys(planData.data.activePlan).length > 0 ? planData.data.activePlan as ActivePlan | null : null;

    const isFirstTest = getItem('userRole') === 'STUDENT' && startedTestId.isFirstTest;

    const getPlanColor = (planName : string | undefined) => {
        let plan = planData?.data?.plans?.find((plan : Plan) => plan?.planName === planName);
        return plan?.additionalProps?.planColor ?? '#3B60E4'
    }

    const getIsPlanDisabled = (planId : string) => {
        if(!activePlanData?.planName) return true;

        const activePlan = planData?.data?.plans?.find(((plan : Plan) => plan?.planName === activePlanData?.planName));
        const currentPlan = planData?.data?.plans?.find((plan : Plan) => plan?.id === planId);

        return currentPlan?.price !== undefined && activePlan?.price !== undefined ? currentPlan.price > activePlan.price : false;
    }

    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return "N/A";
      
        const date = new Date(`${dateString}Z`);
      
        const month = date.toLocaleString("en-US", { month: "short" });
        const day = date.getDate();
        const year = date.getFullYear();
      
        return `${month} ${day}, ${year}`;
    };

    const getDurationRemaining = (endDateStr: string | undefined): string => {
        if (!endDateStr) return "N/A";
      
        const today = new Date();
        const endDate = new Date(endDateStr);
      
        today.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
      
        const timeDiff = endDate.getTime() - today.getTime();
        const dayDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
      
        if (dayDiff < 0) return "Expired";
      
        return `${dayDiff} Day${dayDiff !== 1 ? "s" : ""}`;
    };

    const getFeatureColor = (isOffered: boolean, isDisabled: boolean, color: string) => {
        if (!isOffered) return '#BBBBBD';
        return isDisabled ? color : '#BBBBBD';
    }

    const handleCheckout = (planId: string) => {
        const currentPlan = planData?.data?.plans?.find((plan : Plan) => plan?.id === planId);
        dispatch(setSelectedPlan(currentPlan));
        navigate('/checkout');
    }

    const handleDownloadInvoice = async (transactionId : string) => {
        try{
            const userId = getItem('userRole') ===  'STUDENT' ? getItem('userId') : parentStudentId;
            const response = await downloadInvoice({transactionId,userId});
            let fileName = `invoice_${transactionId}.pdf`;
            downloadBlobFile(response?.data,fileName);
        }catch(err){
            toast(
                ({ closeToast }) => (
                <div className="flex items-center justify-between gap-3 px-3 py-2 font-gully border-2 border-satPrepRed rounded-md bg-white w-full" style={{ boxShadow : '0px 4px 8px 0px rgba(28, 37, 44, 0.08)'}}>
                    <p className='font-gully text-satPrepBlack text-lg'>Failed to download. Please retry.</p>
                    <button onClick={closeToast} className="text-xl hover:text-gray-500">
                        <BiX />
                    </button>
                </div>
                ),
                {
                    closeButton: false,
                    hideProgressBar: true,
                    autoClose: 3000,
                    position: "top-right",
                    className: 'max-w-[400px] w-full mx-4',
                    style: {
                    padding: 0,
                    background: 'transparent',
                    boxShadow: 'none'
                    }
                }
            );
            console.error(err);
        }
    }

    useEffect(() => {
      if(token){
        try{
            let decoded = decode(token);
            setItem("route", `/plans`);
            handleTokenChanges(decoded);
        }catch(err){
            console.error(err);
            navigate('/login');
        }
      }
      return () => {
       getItem('route') === '/plans' && removeItem('route')
      }
    },[token])

    return (
        <DashboardBackground>
            <div className={`p-5 flex gap-[1%] w-full ${isFirstTest ? 'flex-col' : 'flex-row'}`}>
                {/* Sidebar */}
                <SideBarV1 isFirstTest={isFirstTest} />

                <section className={`flex ${!isFirstTest && 'flex-col'} justify-center items-start gap-[2vh]`} style={{width: isFirstTest ? '100%' : "calc(100vw - 240px)",height: isFirstTest ? '' : "calc(100vh - 40px)"}}>
                
                {!isFirstTest && <HeaderV1/>}

                <div className="px-3 py-2 w-full flex flex-grow justify-between items-center self-stretch rounded-[28px] border-[0.5px]  border-satPrepGrey bg-satPrepTransWhite shadow-widget-card backdrop-blur-sevenFive relative" style={{height: "calc(100vh - 12vh - 40px)"}}>
                    <div className="h-full py-1 w-full overflow-y-auto scroll4">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full w-full" >
                            <FullMarksLoader height={150} width={150} />
                        </div>
                        ) : (
                        <div className="flex flex-col gap-9 h-full w-full">
                            <h2 className='text-center font-gully text-satPrepAccent text-[56px] font-bold py-3'>Plans</h2>
                            <div>
                                { activePlanData && (
                                    <div className="mb-3 flex p-6 justify-between items-center self-stretch rounded-[28px] border-2 border-satPrepGreen bg-satPrepFrostedWhite backdrop-blur-sevenFive shadow-widget-card">
                                        <div className="flex flex-col gap-2">
                                            <p className="text-[26px] font-gully font-semibold text-satPrepGreen">
                                            Active Plan:{" "}
                                            <span
                                                style={{
                                                color: getPlanColor(activePlanData?.planName),
                                                }}
                                            >
                                                {activePlanData?.planName ?? '-'}
                                            </span>
                                            </p>
                                            <div className="flex items-center gap-12 text-satPrepDarkGrey font-gully font-medium">
                                            <p>
                                                Date of Purchase:{" "}
                                                {formatDate(activePlanData?.dateOfPurchase)}
                                            </p>
                                            <p>
                                                Tests Remaining:{" "}
                                                {activePlanData?.testsRemaining}
                                            </p>
                                            <p>
                                                Duration Remaining:{" "}
                                                {getDurationRemaining(activePlanData?.planEndDate)}
                                            </p>
                                            </div>
                                        </div>
                                        <div>
                                            <AuthButton
                                                text="Download Invoice"
                                                icon={<BiDownload size={22} />}
                                                iconPosition="left"
                                                onClick={() => handleDownloadInvoice(activePlanData?.transactionId)}
                                                loader={invoiceLoading}
                                            />
                                        </div>
                                    </div>
                                )}
                                {planData?.data?.plans?.length > 0 ? (
                                    <div className="grid w-full grid-cols-1 md:grid-cols-3 gap-[2%]">
                                    {
                                        planData?.data?.plans?.map((plan : Plan, index : number) => (
                                            <div key={plan?.id} className="w-full flex p-6 flex-col font-gully items-end gap-9 self-stretch rounded-[28px] border-2 bg-satPrepFrostedWhite backdrop-blur-[20px]" style={{borderColor : `${getIsPlanDisabled(plan?.id) ? plan.additionalProps?.planColor : '#BBBBBD'}`,boxShadow: `${getIsPlanDisabled(plan?.id) && plan.additionalProps?.boxShadow}`}} >
                                                <div className='flex flex-col gap-6 self-stretch'>
                                                        <div className='flex flex-col gap-3'>
                                                            <p className='text-[28px] font-semibold' style={{color: `${getIsPlanDisabled(plan?.id) ? plan.additionalProps?.planColor : '#BBBBBD'}`}}>{plan?.planName}</p>
                                                            <p className='text-[56px] font-bold text-satPrepXDarkAccent'>{plan?.currencySymbol}{plan?.price}</p>
                                                            <p className='text-sm font-semibold text-satPrepBlack'>{plan?.packageDetails?.targetAudience}</p>
                                                        </div>
                                                        <div className='w-full h-[1px] bg-satPrepGrey'></div>
                                                        <div className='flex flex-col items-start self-stretch gap-[6px]'> 
                                                            <p className='text-xl font-medium text-satPrepBlack pb-1'>Overview</p>
                                                            <div className='flex flex-col gap-[6px]'>
                                                                {
                                                                    plan?.packageDetails?.features.map((feature) => (
                                                                        <div key={feature?.name} className='flex items-center gap-3'>
                                                                            <BiCheckCircle size={24} className='mb-[2px]' style={{color:`${getFeatureColor(feature.isOffered ,getIsPlanDisabled(plan?.id),plan.additionalProps?.planColor) }`}}/>
                                                                            <p className={`text-base ${feature?.isOffered ? 'text-satPrepBlack' : 'text-satPrepGrey'}`}>{feature?.name}</p>
                                                                        </div>
                                                                    ))
                                                                }
                                                            </div>
                                                        </div>
                                                </div>
                                                <div>
                                                    <button className='py-[10px] px-[22px] flex items-center justify-center font-medium text-satPrepWhite rounded-md' disabled={!getIsPlanDisabled(plan?.id)} style={{backgroundColor:`${getIsPlanDisabled(plan?.id) ? plan.additionalProps?.planColor : '#BBBBBD'}`}} onClick={() => handleCheckout(plan?.id)}><span className='py-2'>Purchase Pack</span></button>
                                                </div>
                                            </div>
                                        ))
                                    }
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center w-full">
                                        <p className="text-3xl text-satPrepBlack font-gully font-bold text-center my-5">
                                            No Plans Available
                                        </p>
                                    </div>
                                )}
                           </div>
                           {
                                 (  <div className='flex justify-end'>
                                        <div className='p-3 w-fit rounded-[18px] border-[0.5px] border-satPrepGrey bg-satPrepTransWhite backdrop-blur-sevenFive shadow-widget-card'>
                                            <AuthButton
                                                text="Purchase History"
                                                onClick={() => navigate('/purchasehistory')}
                                                className="font-gully text-lg"
                                                icon={<BiHistory size={22} className="mb-1" />}
                                                iconPosition="left"
                                            />
                                        </div>
                                    </div>
                                )
                            }
                        </div>
                    )}
                    </div>
                </div>

                </section>
            </div>
        </DashboardBackground>
    )
}

export default Plans